import { useState, useCallback, useMemo, useEffect } from 'react';
import type { Table } from '@tanstack/react-table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { FilterDropdownButton } from './FilterDropdownButton';
import { FilterOptionsList } from './FilterOptionsList';
import type { FilterConfig, FilterValue, EditableCellConfig } from '../types';
import { useLingui } from '@lingui/react/macro';

interface FilterDropdownProps<T> {
  filterConfig: FilterConfig<T>;
  columnLabels: Record<string, string>;
  activeFilters: string[];
  onFilterAdd: (columnId: string, value: FilterValue) => void;
  table: Table<T>;
  columns: EditableCellConfig<T>[];
  /**
   * Disables the trigger and force-closes the panel. Used by callers that hold
   * an action lock while a mutation is in flight, so the filter set cannot be
   * changed out from under a request that already captured it.
   */
  disabled?: boolean;
}

/**
 * Dropdown menu interface for adding filters
 * Features a two-column layout with search functionality
 */
export function FilterDropdown<T>({
  filterConfig,
  columnLabels,
  activeFilters,
  onFilterAdd,
  table,
  columns,
  disabled = false,
}: FilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [contentDimensions, setContentDimensions] = useState({ width: 300, height: 200 });
  const { t } = useLingui();

  // A panel left open when the lock engages would still accept clicks.
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const handleFilterApply = useCallback((columnId: string, value: FilterValue) => {
    onFilterAdd(columnId, value);
  }, [onFilterAdd]);

  const handleClosePanelAndDropdown = useCallback(() => {
    setHoveredOption(null);
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  // Calculate filtered options for display
  const filteredOptions = useMemo(() => {
    const options = Object.entries(filterConfig).map(([key]) => ({
      id: key,
      label: columnLabels[key] || key,
      isActive: activeFilters.includes(key),
    }));

    if (!searchQuery) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(query)
    );
  }, [filterConfig, columnLabels, activeFilters, searchQuery]);

  // Calculate optimal dimensions based on content
  useEffect(() => {
    if (!isOpen || filteredOptions.length === 0) return;

    // Calculate width based on longest label + padding
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    // Set font to match the dropdown text
    context.font = '14px system-ui, -apple-system, sans-serif'; // text-sm equivalent

    let maxWidth = 0;
    filteredOptions.forEach(option => {
      const width = context.measureText(option.label).width;
      maxWidth = Math.max(maxWidth, width);
    });

    // Add padding: 24px (icon + gap) + 20px (right padding) + 24px (left/right padding) = 68px
    const calculatedWidth = Math.max(200, Math.min(400, maxWidth + 68));

    // Calculate height based on number of options
    // 44px for search input + 8px padding top/bottom + (32px per option * count) + 8px for list padding
    const searchHeight = 44 + 16; // search input + padding
    const optionHeight = 32; // py-2 + text + padding
    const listPadding = 8; // py-1 on ul
    const calculatedHeight = searchHeight + (filteredOptions.length * optionHeight) + listPadding;

    // Limit maximum height to prevent very tall dropdowns
    const maxHeight = Math.min(calculatedHeight, 400);

    setContentDimensions({
      width: calculatedWidth,
      height: maxHeight
    });
  }, [isOpen, filteredOptions]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery('');
      setHoveredOption(null);
    }
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <FilterDropdownButton isOpen={isOpen} disabled={disabled} />
      </PopoverTrigger>
      <PopoverContent 
        className="p-0" 
        align="start"
        sideOffset={5}
        style={{
          width: `${contentDimensions.width}px`,
          minHeight: `${contentDimensions.height}px`
        }}
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t`Search filters...`}
                className="pl-9 h-9"
                autoFocus
              />
            </div>
          </div>
          
          {/* Filter Options List */}
          <div className="relative">
            <FilterOptionsList
              filterConfig={filterConfig}
              columnLabels={columnLabels}
              activeFilters={activeFilters}
              searchQuery={searchQuery}
              hoveredOption={hoveredOption}
              onOptionHover={setHoveredOption}
              columns={columns}
              onFilterApply={handleFilterApply}
              table={table}
              onClosePanel={handleClosePanelAndDropdown}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}